import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Field, Icon } from '@evogard/ui';
import { looksLikeLocation } from '@evogard/domain';
import type { Place } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';

/** Uma sessão por campo, até a escolha: o Google cobra as letras e a escolha juntas. */
function newSession(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * O endereço com as sugestões do Google enquanto a central digita (Places
 * Autocomplete, pela API da casa — a chave não vem ao navegador). Escolher uma
 * sugestão traz a coordenada; digitar de novo desfaz a escolha.
 *
 * Colar a localização que o associado mandou (29/10/2026) — o link do Google
 * Maps ou do WhatsApp, ou a coordenada solta — não abre sugestão: a API lê a
 * coordenada do link, pergunta ao Google o nome da rua, e o campo já sai
 * localizado. O ponto é o do link, e não o centro da rua.
 */
export function AddressSearch({
  label,
  value,
  place,
  onText,
  onPlace,
}: {
  label: string;
  value: string;
  place: Place | null;
  onText: (text: string) => void;
  onPlace: (place: Place) => void;
}) {
  const [open, setOpen] = useState(false);
  const session = useRef(newSession());
  const term = useDebouncedTerm(value);
  const pasted = looksLikeLocation(value);
  const searching = open && !place && !pasted && term.trim().length >= 3;

  const suggestions = useQuery({
    queryKey: ['assistencia', 'enderecos', term],
    enabled: searching,
    staleTime: 60_000,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.placesAutocomplete({
          body: { input: term.trim(), session: session.current },
        }),
        200,
        'Não foi possível buscar no Google.',
      ),
  });
  const pick = useMutation({
    mutationFn: async (placeId: string) =>
      ok(
        await api.assistanceDispatches.placeDetails({
          body: { placeId, session: session.current },
        }),
        200,
        'Não foi possível abrir o endereço escolhido.',
      ),
    onSuccess: (p) => {
      onPlace(p);
      setOpen(false);
      session.current = newSession();
    },
  });
  const fromLocation = useMutation({
    mutationFn: async (text: string) =>
      ok(
        await api.assistanceDispatches.placeFromLocation({ body: { text } }),
        200,
        'Não foi possível ler esta localização.',
      ),
    onSuccess: (p) => {
      onPlace(p);
      setOpen(false);
    },
  });
  useEffect(() => {
    if (place) setOpen(false);
  }, [place]);

  const items = suggestions.data ?? [];
  return (
    <div className="relative">
      <Field
        label={label}
        required
        autoComplete="off"
        value={value}
        onChange={(e) => {
          const text = e.target.value;
          onText(text);
          setOpen(true);
          if (looksLikeLocation(text)) fromLocation.mutate(text.trim());
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        valid={place ? 'Localizado no mapa' : undefined}
        error={
          suggestions.error?.message ??
          pick.error?.message ??
          (pasted ? fromLocation.error?.message : null) ??
          null
        }
        help={
          fromLocation.isPending
            ? 'Lendo a localização no Google…'
            : !place && value.trim().length > 0 && value.trim().length < 3
              ? 'Digite pelo menos 3 letras.'
              : !place && value.trim() === ''
                ? 'Endereço, ou o link de localização do WhatsApp ou do Maps.'
                : undefined
        }
      />
      {searching && (items.length > 0 || suggestions.isFetching) && (
        <ul
          role="listbox"
          aria-label={`Sugestões para ${label}`}
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-lg border border-borda-forte bg-superficie py-1 shadow-lg"
        >
          {suggestions.isFetching && items.length === 0 && (
            <li className="px-3 py-2 text-sm text-texto-tenue">Buscando no Google…</li>
          )}
          {items.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-superficie-suave"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick.mutate(s.placeId)}
              >
                <Icon
                  nome="mapa"
                  size={16}
                  className="mt-0.5 shrink-0 text-texto-tenue"
                />
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
